// Routine editor (modal): a French phrase that fills the planning (POST /api/routines/parse), agent,
// title, instructions, the schedule builder with its live description, autonomy with what each
// choice means, output format, engine & model, active switch. Used by the automations panel and the
// calendar (click on a routine), and by the agent drawer (the agent locked).
import { h, button, field, input, textarea, switchToggle, uid, setText, select } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import {
  routineForm, validateRoutineForm, routineBody, firstLineOf, ROUTINE_TITLE_MAX, ROUTINE_PROMPT_MAX, ENGINES, MODELS, EFFORTS,
  engineHintKey,
} from './model.js';
import { scheduleToBuilder, defaultBuilder, describeSchedule, builderToSchedule } from './schedule.js';
import { createScheduleBuilder } from './schedulebuilder.js';
import { agentSelect, autonomyControl, formatControl, counter } from './widgets.js';
import './strings.js';

/**
 * Opens the routine editor.
 * @param {object} ctx app context
 * @param {{routine?: object|null, defaults?: object, lockAgent?: boolean, onSaved?: (routine: object) => void,
 *   onDeleted?: (id: string) => void}} [opts]
 *   defaults: {agentId, title, prompt, autonomy, schedule, builder} for a new routine; lockAgent: the
 *   agent cannot be changed (opened from an agent's drawer)
 * @returns {import('../overlay.js').Layer}
 */
export function openRoutineEditor(ctx, { routine = null, defaults = {}, lockAgent = false, onSaved, onDeleted } = {}) {
  const editing = !!routine?.id;
  const state = ctx.store.get();
  const form = routineForm(routine, defaults);
  const agentOf = (id) => ctx.store.get().office.agents.find((a) => a.id === id) || null;
  let layer = null;
  let busy = false;

  // --- phrase ------------------------------------------------------------------------------------
  const phraseInput = input({ placeholder: t('auto.re.phrasePlaceholder'), maxLength: 400, autocomplete: 'off' });
  const phraseStatus = h('p', { class: 'auto-phrase-status', 'aria-live': 'polite', hidden: true });
  const phraseBtn = button(t('auto.re.phraseFill'), { variant: 'secondary', size: 'sm', icon: 'sparkle', onClick: () => parsePhrase() });
  const phraseField = field(t('auto.re.phrase'), phraseInput, { hint: t('auto.re.phraseHint'), optional: true });
  const phraseBox = h('div', { class: 'auto-phrase' }, h('div', { class: 'auto-phrase-row' }, phraseField, phraseBtn), phraseStatus);

  // --- main fields ---------------------------------------------------------------------------------
  const agentEl = agentSelect(state, { need: 'manage', value: form.agentId, extra: editing || lockAgent ? agentOf(form.agentId) : null });
  const locked = lockAgent && !!form.agentId && agentEl.value === form.agentId;
  if (locked) agentEl.disabled = true;
  const agentField = field(t('auto.agent'), agentEl, locked ? { hint: t('auto.re.agentLocked') } : {});
  const titleInput = input({ value: form.title, maxLength: ROUTINE_TITLE_MAX + 20, placeholder: t('auto.re.titlePlaceholder') });
  const titleField = field(t('auto.re.title'), titleInput);
  titleField.querySelector('.field-label')?.appendChild(counter(titleInput, ROUTINE_TITLE_MAX));
  const promptInput = textarea({ value: form.prompt, rows: 5, maxLength: ROUTINE_PROMPT_MAX + 200, placeholder: t('auto.re.promptPlaceholder') });
  const promptField = field(t('auto.re.prompt'), promptInput);
  promptField.querySelector('.field-label')?.appendChild(counter(promptInput, ROUTINE_PROMPT_MAX));

  const initialBuilder = scheduleToBuilder(routine?.schedule || defaults.schedule || null, defaults.builder || defaultBuilder());
  const sched = createScheduleBuilder(initialBuilder, { onChange: () => clearError('schedule') });
  const schedError = h('p', { class: 'field-error', role: 'alert', hidden: true });

  const autonomy = autonomyControl({ value: form.autonomy, agent: () => agentOf(agentEl.value) });
  const format = formatControl({ value: form.outputFormat });

  const engineEl = select([['', t('auto.engineDefault')], ...ENGINES.map((e) => [e, t(`auto.engine.${e}`)])], { value: form.engine });
  const modelListId = uid('models');
  const modelEl = input({ value: form.model, list: modelListId, placeholder: t('auto.modelDefault'), maxLength: 80 });
  const effortEl = select([['', t('auto.effortDefault')], ...EFFORTS.map((e) => [e, t(`auto.effort.${e}`)])], { value: form.effort });
  // Gemini and Images OpenAI have no tools: say so when they are chosen.
  const engineHint = h('p', { class: 'field-hint auto-engine-hint', hidden: true });
  function syncEngineHint() {
    const key = engineHintKey(engineEl.value);
    engineHint.hidden = !key;
    setText(engineHint, key ? t(key) : '');
  }
  engineEl.addEventListener('change', syncEngineHint);
  syncEngineHint();
  const advanced = h('details', { class: 'auto-advanced', open: !!(form.engine || form.model || form.effort) },
    h('summary', { class: 'mono-label' }, t('auto.advanced')),
    h('div', { class: 'auto-sched-row' }, field(t('auto.engine'), engineEl), field(t('auto.model'), modelEl), field(t('auto.effort'), effortEl)),
    engineHint,
    h('datalist', { id: modelListId }, MODELS.map((m) => h('option', { value: m }))));

  const activeSwitch = switchToggle(t('auto.re.active'), { checked: !form.paused, hint: t('auto.re.activeHint') });
  const formError = h('p', { class: 'auto-form-error', role: 'alert', hidden: true });

  const fields = { agentId: agentField, title: titleField, prompt: promptField };
  function setFieldError(name, msg) {
    if (name === 'schedule') {
      setText(schedError, msg || '');
      schedError.hidden = !msg;
      return;
    }
    fields[name]?.setError(msg || null);
  }
  function clearError(name) { setFieldError(name, null); }
  function setFormError(msg) {
    setText(formError, msg || '');
    formError.hidden = !msg;
  }

  agentEl.addEventListener('change', () => { clearError('agentId'); autonomy.refresh(); });
  titleInput.addEventListener('input', () => clearError('title'));
  promptInput.addEventListener('input', () => clearError('prompt'));
  phraseInput.addEventListener('keydown', (e) => { if (e.key === 'Enter' && !e.isComposing) { e.preventDefault(); parsePhrase(); } });

  // --- phrase parsing ------------------------------------------------------------------------------
  let parseSeq = 0;
  function setPhraseStatus(text, tone) {
    setText(phraseStatus, text || '');
    phraseStatus.hidden = !text;
    phraseStatus.className = `auto-phrase-status${tone ? ` is-${tone}` : ''}`;
  }
  async function parsePhrase() {
    const text = phraseInput.value.trim();
    if (!text) { phraseInput.focus(); return; }
    const seq = ++parseSeq;
    setPhraseStatus(t('auto.re.phraseReading'), 'pending');
    phraseBtn.disabled = true;
    try {
      const res = await ctx.api.post('/api/routines/parse', { text });
      if (seq !== parseSeq || !layer?.isOpen()) return;
      if (!res || res.error || !res.schedule) { setPhraseStatus(res?.error || t('auto.re.phraseInvalid'), 'error'); return; }
      sched.setBuilder(scheduleToBuilder(res.schedule, sched.builder));
      const rest = String(res.rest || '').trim();
      if (rest && !promptInput.value.trim()) { promptInput.value = rest; promptInput.dispatchEvent?.(new Event('input')); }
      if (rest && !titleInput.value.trim()) { titleInput.value = firstLineOf(rest); titleInput.dispatchEvent?.(new Event('input')); }
      if (Array.isArray(res.problems) && res.problems.length) setPhraseStatus(String(res.problems[0]), 'error');
      else if (res.missing === 'day') setPhraseStatus(t('auto.re.phraseMissingDay'), 'warn');
      else if (res.missing === 'time') setPhraseStatus(t('auto.re.phraseMissingTime'), 'warn');
      else setPhraseStatus(t('auto.re.phraseOk', { description: res.description || describeSchedule(res.schedule) }), 'ok');
    } catch (err) {
      if (seq !== parseSeq) return;
      setPhraseStatus(err.status === 404 ? t('auto.re.phraseUnavailable') : err.message, 'error');
    } finally {
      if (seq === parseSeq) phraseBtn.disabled = false;
    }
  }

  // --- save / delete -------------------------------------------------------------------------------
  const saveBtn = button(editing ? t('auto.re.save') : t('auto.re.create'), { variant: 'primary', type: 'submit', onClick: (e) => { e.preventDefault(); save(); } });
  const cancelBtn = button(t('common.cancel'), { variant: 'ghost', onClick: () => layer?.close('cancel') });
  const canDelete = editing && ctx.can('manage', { deptId: routine.deptId });
  const deleteBtn = canDelete ? button(t('auto.re.delete'), { variant: 'ghost', icon: 'close', class: 'auto-delete', onClick: () => remove() }) : null;

  function readForm() {
    form.agentId = agentEl.value;
    form.title = titleInput.value;
    form.prompt = promptInput.value;
    form.autonomy = autonomy.value;
    form.outputFormat = format.value;
    form.engine = engineEl.value;
    form.model = modelEl.value;
    form.effort = effortEl.value;
    form.paused = !activeSwitch.input.checked;
  }

  async function save() {
    if (busy) return;
    readForm();
    setFormError('');
    const builder = sched.builder;
    const errors = validateRoutineForm(form, builder, ctx.store.get());
    for (const name of ['agentId', 'title', 'prompt', 'schedule']) setFieldError(name, errors[name]);
    const firstBad = ['agentId', 'title', 'prompt', 'schedule'].find((n) => errors[n]);
    if (firstBad) {
      setFormError(t('auto.fixErrors'));
      if (firstBad === 'agentId') agentEl.focus();
      else if (firstBad === 'title') titleInput.focus();
      else if (firstBad === 'prompt') promptInput.focus();
      else sched.focusFirstInvalid();
      return;
    }
    busy = true;
    saveBtn.disabled = true;
    saveBtn.classList.add('is-busy');
    layer.setBusy?.(true);
    try {
      const body = routineBody(form, builder, ctx.store.get(), routine);
      const res = editing
        ? await ctx.api.patch(`/api/routines/${encodeURIComponent(routine.id)}`, body)
        : await ctx.api.post('/api/routines', body);
      const saved = res?.routine && typeof res.routine === 'object' ? res.routine : (res && typeof res === 'object' ? res : { ...body });
      if (editing) ctx.toast.success(t('auto.re.saved'));
      else ctx.toast.success(t('auto.re.created', { description: saved.description || describeSchedule(builderToSchedule(builder)) }));
      try { onSaved?.(saved); } catch (err) { console.error(err); }
      layer.close('confirm');
    } catch (err) {
      setFormError(err.message || t('auto.saveError'));
    } finally {
      busy = false;
      saveBtn.disabled = false;
      saveBtn.classList.remove('is-busy');
      layer?.setBusy?.(false);
    }
  }

  async function remove() {
    const ok = await ctx.overlay.confirm({
      title: t('auto.routines.deleteTitle'), message: t('auto.routines.deleteText', { title: routine.title || '' }),
      confirmLabel: t('auto.routines.delete'), danger: true,
    });
    if (!ok) return;
    try {
      await ctx.api.del(`/api/routines/${encodeURIComponent(routine.id)}`);
      ctx.toast.success(t('auto.routines.deleted'));
      try { onDeleted?.(routine.id); } catch (err) { console.error(err); }
      layer.close('confirm');
    } catch (err) {
      setFormError(err.message);
    }
  }

  const content = h('form', { class: 'auto-form stack', novalidate: true, onSubmit: (e) => { e.preventDefault(); save(); } },
    phraseBox,
    h('div', { class: 'auto-grid2' }, agentField, titleField),
    promptField,
    sched.el, schedError,
    h('div', { class: 'auto-grid2' }, autonomy.el, format.el),
    advanced,
    activeSwitch,
    formError);

  layer = ctx.overlay.openModal({
    title: editing ? t('auto.re.titleEdit') : t('auto.re.titleNew'),
    eyebrow: editing ? (routine.title || '') : null,
    size: 'lg', className: 'auto-modal', content,
    footer: [deleteBtn, h('span', { class: 'spacer' }), cancelBtn, saveBtn],
    initialFocus: editing ? titleInput : phraseInput,
  });
  return layer;
}
